import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import type { Column, RowData } from "@tanstack/react-table";
import type React from "react";
import { useState } from "react";

import type { features } from "./features";

interface ColumnMenuProps<R extends RowData> {
  column: Column<typeof features, R, unknown>;
  onFilter: () => void;
  onManageColumns: () => void;
  onOpenChange: (open: boolean) => void;
  triggerIcon: React.ReactNode;
}

export default function ColumnMenu<R extends RowData>({
  column,
  onFilter,
  onManageColumns,
  onOpenChange,
  triggerIcon,
}: ColumnMenuProps<R>) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const close = () => {
    setAnchor(null);
    onOpenChange(false);
  };
  const sorted = column.getIsSorted();
  const pinned = column.getIsPinned();
  const run = (action: () => void) => () => {
    action();
    close();
  };

  return (
    <>
      <IconButton
        size="small"
        aria-label="Menu"
        aria-haspopup="menu"
        onClick={(event) => {
          setAnchor(event.currentTarget);
          onOpenChange(true);
        }}
      >
        {triggerIcon}
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
        {column.getCanSort() && [
          sorted && (
            <MenuItem key="unsort" onClick={run(() => column.clearSorting())}>
              <ListItemIcon />
              <ListItemText>Unsort</ListItemText>
            </MenuItem>
          ),
          sorted !== "asc" && (
            <MenuItem
              key="asc"
              onClick={run(() => column.toggleSorting(false, false))}
            >
              <ListItemIcon>
                <ArrowUpwardIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Sort by ASC</ListItemText>
            </MenuItem>
          ),
          sorted !== "desc" && (
            <MenuItem
              key="desc"
              onClick={run(() => column.toggleSorting(true, false))}
            >
              <ListItemIcon>
                <ArrowDownwardIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Sort by DESC</ListItemText>
            </MenuItem>
          ),
          <Divider key="sort-divider" />,
        ]}
        <MenuItem onClick={run(onFilter)}>
          <ListItemIcon>
            <FilterAltIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Filter</ListItemText>
        </MenuItem>
        <Divider />
        {pinned !== "start" && (
          <MenuItem onClick={run(() => column.pin("start"))}>
            <ListItemIcon>
              <PushPinOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Pin to left</ListItemText>
          </MenuItem>
        )}
        {pinned !== "end" && (
          <MenuItem onClick={run(() => column.pin("end"))}>
            <ListItemIcon>
              <PushPinOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Pin to right</ListItemText>
          </MenuItem>
        )}
        {pinned && (
          <MenuItem onClick={run(() => column.pin(false))}>
            <ListItemIcon>
              <PushPinIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Unpin</ListItemText>
          </MenuItem>
        )}
        <Divider />
        {column.getCanHide() && (
          <MenuItem onClick={run(() => column.toggleVisibility(false))}>
            <ListItemIcon>
              <VisibilityOffIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Hide column</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={run(onManageColumns)}>
          <ListItemIcon>
            <ViewColumnIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Manage columns</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
